import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form, Grid, Header, Message, Modal, Segment, SegmentGroup, Table } from "semantic-ui-react";
import type { DropdownProps } from "semantic-ui-react";
import Map, { Layer, LayerProps, MapRef, Source } from "react-map-gl/mapbox";
import { bbox } from "@turf/turf";
import Tooltip from "rc-tooltip";
import _ from "lodash";

import {
	type DataLayerType,
	type PreprocessedFeatureCollectionProps,
	type UUID,
	type VectorDataLayerValuesMapping,
	type VectorDataLayerUploadPreprocResult,
	type VectorDataLayerUploadStdResult,
	type VectorDataLayerRequest,
	type DataLayerUpload,
	type StdFeatureCollectionProps,
	type AttributeTableValue,
} from "../../../../apis/types/dataLayerTypes";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { ApiError, updateVectorDataLayerUpload } from "../../../../apis/dataLayerApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { AerialPlotResponse } from "../../../../apis/types";
import { DataDrivenPropertyValueSpecification } from "mapbox-gl";
import { formatInt } from "../../../../utilities/fmt";
import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useFieldAssociationConfirmation } from "../../../../hooks/useFieldAssociationConfirmation";

import { FIELD_ASSOC_RESET_HEADER, FIELD_ASSOC_RESET_BODY } from "./constants";
import styles from "./styles.module.css";

export interface VectorConfiguratorModalProps {
	upload: DataLayerUpload; // DataLayerUploads record for tracking state
	existingUploads: DataLayerUpload[]; // existing, for validation
	// preProcessedGeoJson: Fetched UrlPath GeoJSON with intermediate feature collection metadata props (same as response from POST upload)
	preProcessedGeoJson: VectorDataLayerUploadPreprocResult;
	// stdGeoJson: Fetched PreprocessedDataUrlPath GeoJSON with aerialFARMS standardized metadata props.
	stdGeoJson?: VectorDataLayerUploadStdResult;
	vectorDataType: DataLayerType; // detail of upload's vector data type
	dataLayerHasExistingFieldAssociations: boolean;
	onResetExistingFieldAssociations: () => void;
	onSaved: (uploadId: UUID, response: AerialPlotResponse<VectorDataLayerUploadPreprocResult>) => void; // called when Save request is completed
	onClose: () => void; // called when Close button pressed
	onCancel: () => void; // called when Cancel button pressed
}

/**
 * Maximum number of categories the UI flow is supporting.
 */
const MAX_ZONES = 12;

/**
 * Number of source attribute columns to preview by default at load.
 */
const NUM_COLS_DEFAULT = 3;

/**
 * Reserved value for system (must have description "unassigned")
 */
const UNASSIGNED_ID = 0;
const UNASSIGNED_DESCR = "unassigned";
const DEFAULT_ATTR_VALUE_DESCR_OPTIONS = [
	{
		key: UNASSIGNED_DESCR,
		value: UNASSIGNED_DESCR,
		text: UNASSIGNED_DESCR,
	},
];

/**
 * Dropdown menu options for mapbox overlay.
 */
const MAP_VIS_ATTR_TARGET_OPTIONS = [
	{
		key: "raw",
		text: "Source (raw) attributes",
		value: "raw",
	},
	{
		key: "std",
		text: "Assigned (aerialFARMS) attributes",
		value: "std",
	},
];

/**
 * Only supporting 1 layer currently (from a raw vector data file)
 */
const LAYER_NUM = 0;

/**
 * Number of features to preview in Attribute values from data source table.
 */
const PREVIEW_TOP_N_FEATURES = 100;

/**
 * Transparency- used for aerialfarms_std = 0 (unassigned)
 */
const TRANSPARENT_COLOR = "rgba(0,0,0,0)";

const ColorChip = ({ bgColor }: { bgColor: string }) => (
	<div style={{ backgroundColor: bgColor }} className={styles.colorChip} />
);

const TransparentColorChip = () => <div className={`${styles.colorChip} ${styles.checkerboard}`} />;

/**
 * Enum for switching between attribute table visualizations on mapbox.
 */
enum AttrVis {
	RAW = "raw",
	STD = "std",
}

/**
 * Enum of columns in the configure attribute columns table.
 */
enum ConfigAttrTableColumn {
	SRC_VALS = "srcVals",
	STD_IDS = "stdIds",
	STD_DESCR = "stdDescr",
}

/**
 * Which section to hi-light if there is a validation error. Errors are generated one at a time.
 */
export enum ValidationSection {
	IdColumn = 0,
	DescriptionColumn = 1,
	SelectCategoryNumber = 2,
}

/**
 * Vector Data Layer Upload configurator.
 * Wraps semantic-ui's Modal. Interaction is strict: forces user to pick Save/Apply or Cancel.
 */
const VectorConfiguratorModal = ({
	upload,
	existingUploads,
	preProcessedGeoJson,
	stdGeoJson,
	vectorDataType,
	dataLayerHasExistingFieldAssociations,
	onResetExistingFieldAssociations,
	onSaved,
	onCancel,
}: VectorConfiguratorModalProps) => {
	const mapRef = useRef<MapRef>(null);
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };

	const {
		fieldAssocModalIsOpen,
		handleFieldAssocModalOk,
		handleFieldAssocModalCancel,
		withFieldAssociationConfirmation,
	} = useFieldAssociationConfirmation(dataLayerHasExistingFieldAssociations, onResetExistingFieldAssociations);

	const preprocessedLayerMetadata = (
		preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps }
	).properties;

	/*
	 * Pre-State setup -- compute derived values
	 */

	// Compute initial selectedAttrCol from props (used by multiple state initializers)
	const initialSelectedAttrCol = (() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.rawAttributeFieldName;
		}
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		return layerMetadata.attributeTables![LAYER_NUM].fields.fields[0];
	})();

	// Sorted unique attribute values for a given column
	const getSortedUniqueAttrVals = useCallback(
		(attrCol: string): AttributeTableValue[] =>
			_.sortedUniq(
				_.sortBy(
					preProcessedGeoJson.features.map((feature): AttributeTableValue => {
						if (feature.properties) {
							return feature.properties[attrCol] as AttributeTableValue;
						}
						return false;
					}),
				),
			),
		[preProcessedGeoJson.features],
	);

	/*
	 * State
	 */

	// number of categories or zones user is targeting
	const [numZones, setNumZones] = useState<number | undefined>(() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.attributeDescriptions.length - 1;
		}
		const sortedUniqueAttrVals = getSortedUniqueAttrVals(initialSelectedAttrCol);
		return _.min([MAX_ZONES, sortedUniqueAttrVals.length]) ?? 1;
	});

	// attribute columns to display in source data table
	const [selectedSourceAttrCols, setSelectedSourceAttrCols] = useState<string[]>(() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return [stdLayerMetadata.rawAttributeFieldName];
		}
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		const allFields = layerMetadata.attributeTables![LAYER_NUM].fields.fields;
		return [...allFields].slice(0, NUM_COLS_DEFAULT);
	});

	// selected attribute column aka attribute field (hilited column in source attrs data table)
	const [selectedAttrCol, setSelectedAttrCol] = useState<string | undefined>(initialSelectedAttrCol);

	// current configuration of categorical assignment of attributes for selected attribute column
	const [valuesMapping, setValuesMapping] = useState<VectorDataLayerValuesMapping[]>(() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.rawValuesMapping;
		}
		const sortedUniqueAttrVals = getSortedUniqueAttrVals(initialSelectedAttrCol);
		// numZones isn't readable here, so recompute it
		const initNumZones = _.min([MAX_ZONES, sortedUniqueAttrVals.length]) ?? 1;
		return sortedUniqueAttrVals.map((attrVal, i) => {
			if (i < initNumZones) {
				const id = i + 1;
				return [attrVal, id, `(enter description for ID ${id})`] as VectorDataLayerValuesMapping;
			} else {
				return [attrVal, UNASSIGNED_ID, UNASSIGNED_DESCR] as VectorDataLayerValuesMapping;
			}
		});
	});

	// is PUT mutation in progress
	const [isSaving, setIsSaving] = useState<boolean>(false);

	// prevent user from always seeing validation message from moment modal is opened
	const [isValidationMsgShown, setIsValidationMsgShown] = useState<boolean>(false);

	// track if Save is needed
	const [formIsDirty, setFormIsDirty] = useState<boolean>(false);

	// column sorting flags - preview attribute table
	const [previewAttrTableSortColumn, setPreviewAttrTableSortColumn] = useState<string | undefined>(undefined);
	const [previewAttrTableSortColumnDir, setPreviewAttrTableSortColumnDir] = useState<boolean>(false);

	// column sorting flags - assign std attribute values table
	const [configAttrTableSortColumn, setConfigAttrTableSortColumn] = useState<ConfigAttrTableColumn>(
		ConfigAttrTableColumn.SRC_VALS,
	);
	const [configAttrTableSortColumnDir, setConfigAttrTableSortColumnDir] = useState<boolean>(false);

	// user selection of which attribute table to visualize on mapbox
	const [mapAttributesTarget, setMapAttributesTarget] = useState<AttrVis>(
		stdGeoJson !== undefined ? AttrVis.STD : AttrVis.RAW,
	);

	/*
	 * Queries and Mutations
	 */
	const { mutateAsync: updateVectorDataLayerUploadMutationAsync } = useMutation({
		mutationFn: updateVectorDataLayerUpload,
		onSettled: () => {
			setIsSaving(false);
		},
	});

	/*
	 * Memoization
	 */

	const sortedValuesMapping: VectorDataLayerValuesMapping[] = useMemo(() => {
		if (!selectedAttrCol) {
			return [];
		}
		let sorted: VectorDataLayerValuesMapping[];

		if (configAttrTableSortColumn === ConfigAttrTableColumn.SRC_VALS) {
			sorted = _.sortBy(valuesMapping, ([srcVal]) => srcVal);
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_IDS) {
			sorted = _.sortBy(valuesMapping, ([, stdId]) => stdId);
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_DESCR) {
			sorted = _.sortBy(valuesMapping, ([, , stdDescr]) => stdDescr);
		} else {
			sorted = [...valuesMapping];
		}
		if (configAttrTableSortColumnDir) {
			sorted.reverse();
		}
		return sorted;
	}, [valuesMapping, selectedAttrCol, configAttrTableSortColumn, configAttrTableSortColumnDir]);

	// Lookup table of categorical colors for the config attribute feature table.
	// Use the stdId (integer value) for indexing lookup.
	const targetAttrColors = useMemo(() => {
		// the colorbrewer2 pallets only have between 3-9 greys
		let numColors = _.min([numZones, 9]) ?? 3;
		numColors = _.max([numColors, 3]) ?? 3;
		const greys = COLOR_BREWER2.Greys[String(numColors) as keyof typeof COLOR_BREWER2.Greys] as string[];
		// prefix with transparent color in position 0
		const pallette = [TRANSPARENT_COLOR].concat(greys);
		return pallette;
	}, [numZones]);

	// List of categorical colors for the source feature table.
	const sourceFeatureColors = useMemo(() => {
		const numColors = _.min([preProcessedGeoJson.features.length, MAX_ZONES]) ?? 0;
		const pallette = COLOR_BREWER2.Set3["12"];
		return preProcessedGeoJson.features.map((_, i) => {
			if (i >= numColors || i > pallette.length) {
				return "grey";
			}
			return pallette[i];
		});
	}, [preProcessedGeoJson]);

	// Generate preprocessed GeoJson data, decorating the feature properties with attributes for visualization
	// for preview color of raw/source attribute table, just arbitrarily assign categorical colors
	// for preview colors of std/normal attribute table, grey-scales are assigned user-specified zone assignments
	const previewGeoJson = useMemo(() => {
		const stdIdLookup: Record<string | number, number> = {};
		valuesMapping.forEach(([srcVal, stdId]) => {
			if (typeof srcVal === "boolean") {
				stdIdLookup[String(srcVal)] = stdId;
			} else {
				stdIdLookup[srcVal] = stdId;
			}
		});
		const features = preProcessedGeoJson.features.map((feature, i) => {
			const previewAttrsColorIdx = i;
			let aerialFarmsStdId = 1;
			if (selectedAttrCol !== undefined) {
				const srcVal = feature.properties![selectedAttrCol] as string | number;
				aerialFarmsStdId = stdIdLookup[srcVal];
			}
			return {
				...feature,
				properties: {
					...feature.properties,
					previewAttrsColorIdx,
					aerialfarms_std: aerialFarmsStdId,
				} as Record<string, unknown> & { previewAttrsColorIdx: number; aerialfarms_std: number },
			};
		});
		return {
			...preProcessedGeoJson,
			features,
		};
	}, [preProcessedGeoJson, selectedAttrCol, valuesMapping]);

	// for performance, don't display unlimited features in the source attributes table
	const topNGeoJsonFeatures = useMemo(() => {
		const features = previewGeoJson.features.slice(0, PREVIEW_TOP_N_FEATURES);
		if (!previewAttrTableSortColumn) {
			return features;
		}
		const sorted = _.sortBy(features, (f) => f.properties[previewAttrTableSortColumn]);
		if (previewAttrTableSortColumnDir) {
			sorted.reverse();
		}
		return sorted;
	}, [previewGeoJson, previewAttrTableSortColumn, previewAttrTableSortColumnDir]);

	// display a note if the list of features is truncated
	const topNGeoJsonFeaturesDescr = useMemo(() => {
		return topNGeoJsonFeatures.length === preProcessedGeoJson.features.length
			? ""
			: ` (Top ${PREVIEW_TOP_N_FEATURES} Features)`;
	}, [topNGeoJsonFeatures, preProcessedGeoJson.features.length]);

	const vectorDataTypeLabel = useMemo<string>(() => {
		if (vectorDataType.presetName) {
			return vectorDataType.presetName;
		}
		return vectorDataType.name;
	}, [vectorDataType]);

	// Options data modal for DDL configuring source attributes data table.
	const sourceAttrColsSelectOptions = useMemo(() => {
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		const selectOptions = layerMetadata.attributeTables![LAYER_NUM].fields.fields.map((attrCol, i) => ({
			key: `${attrCol}-${i}`,
			value: attrCol,
			text: attrCol,
		}));
		const sorted = _.sortBy(selectOptions, ["text"]);
		return sorted;
	}, [preProcessedGeoJson]);

	// Preset list of possible zones this UI flow supports.
	const categoryNumberSelectOptions = useMemo(() => {
		const length = sortedValuesMapping.length;
		return Array.from({ length }, (_, i) => ({ key: String(i), value: String(i + 1), text: `${i + 1}-zone` }));
	}, [sortedValuesMapping]);

	// Initial DDL options for input of attribute values (IDs). Constrained to integers [0, numZones].
	const targetAttrValuesSelectIDOptions = useMemo(() => {
		if (!numZones) {
			return [];
		}
		return Array.from({ length: numZones + 1 }, (_, i) => ({
			key: i,
			value: String(i),
			text: String(i),
		}));
	}, [numZones]);

	// Initial DDL options for input of attribute values (suggested descriptions)
	const targetAttrValuesSelectDescrOptions = useMemo(() => {
		if (valuesMapping.length === 0) {
			return DEFAULT_ATTR_VALUE_DESCR_OPTIONS;
		}
		const userDescriptions = _.sortedUniq(
			_.sortBy(
				valuesMapping
					.filter(([, id, descr]) => id !== UNASSIGNED_ID && descr.toLowerCase() !== UNASSIGNED_DESCR)
					.map(([, , descr]) => descr),
			),
		);
		const userOptions = userDescriptions.map((descr, i) => ({ key: `user-descr-${i}`, value: descr, text: descr }));
		return [...DEFAULT_ATTR_VALUE_DESCR_OPTIONS, ...userOptions];
	}, [valuesMapping]);

	// Dynamic label above the attribute table
	const attributeAssignmentTooltip = useMemo(() => {
		return `aerialFARMS ID (integer: 0-${numZones}). 0 is a reserved value for '${UNASSIGNED_DESCR}'`;
	}, [numZones]);

	// Generate optional validation message and section to hi-light
	const validationMsg = useMemo(() => {
		// check uniqueness constraint of DataLayerTypeId x Num Zones for current data layer
		if (
			_.some(existingUploads, (existingUpload) => {
				if (
					existingUpload.id !== upload.id &&
					existingUpload.dataLayerTypeId === vectorDataType.id &&
					existingUpload.vectorDataNumZones === numZones
				) {
					return true;
				}
				return false;
			})
		) {
			const msg = `Data layer already has type: ${vectorDataTypeLabel} (${numZones} zone).`;
			return [msg, ValidationSection.SelectCategoryNumber] as const;
		}

		const uniqStdIds = _.uniq(valuesMapping.map(([, stdId]) => stdId).filter((stdId) => stdId > 0));
		if (numZones && uniqStdIds.length != numZones) {
			const intro = uniqStdIds.length < numZones ? "Missing" : "Too many";
			const msg = `${intro} IDs: expected ${numZones} categories or zones, but ${uniqStdIds.length} unique IDs have been entered.`;
			return [msg, ValidationSection.IdColumn] as const;
		}
		const uniqStdDescriptions = _.uniq(
			valuesMapping
				.map(([, , stdDescr]) => stdDescr)
				.filter((stdDescr) => stdDescr !== "" && stdDescr !== UNASSIGNED_DESCR),
		);
		if (numZones && uniqStdDescriptions.length != numZones) {
			const intro = uniqStdDescriptions.length < numZones ? "Missing" : "Too many";
			const msg = `${intro} descriptions: Expected ${numZones} categories or zones, but ${uniqStdDescriptions.length} unique descriptions have been entered.`;
			return [msg, ValidationSection.DescriptionColumn] as const;
		}
		return undefined;
	}, [valuesMapping, numZones, existingUploads, vectorDataType, vectorDataTypeLabel, upload.id]);

	// Generate mapbox layer style props, conditional on which attribute table currently visualizing.
	const mapLayerStyles = useMemo(() => {
		const colorMatchExpr: DataDrivenPropertyValueSpecification<string> =
			mapAttributesTarget === AttrVis.STD
				? [
						"match",
						["get", "aerialfarms_std"],
						..._.flatMap(targetAttrColors, (color, i) => [i, color]),
						"rgba(0,0,0,0)",
					]
				: [
						"match",
						["get", "previewAttrsColorIdx"],
						..._.flatMap(sourceFeatureColors, (color, i) => [i, color]),
						"rgba(0,0,0,0)",
					];
		const result: LayerProps = {
			id: "preview-attrs-layer",
			type: "fill",
			paint: {
				"fill-color": colorMatchExpr,
				"fill-opacity": 0.9,
				"fill-outline-color": "#222",
			},
		};
		return result;
	}, [targetAttrColors, sourceFeatureColors, mapAttributesTarget]);

	// Generate formatted string for feature count
	const featureCountFmt = useMemo(() => {
		const n = preprocessedLayerMetadata?.featureCount?.[LAYER_NUM];
		const result = n ? formatInt(n) : "";
		return result;
	}, [preprocessedLayerMetadata]);

	// Generate formatted string for plural label
	const fileNamesFmt = useMemo(() => {
		if (preprocessedLayerMetadata.fileNames.length > 1) {
			return "File Names";
		}
		return "File Name";
	}, [preprocessedLayerMetadata]);

	// Generate list of file names in vector data upload
	const fileNames = useMemo(() => {
		return preprocessedLayerMetadata.fileNames?.map((fileName) => <div key={fileName}>{fileName}</div>);
	}, [preprocessedLayerMetadata]);

	// Generate formatted string for geometry type(s) label
	const geometryTypesLabelFmt = useMemo(() => {
		if (preprocessedLayerMetadata.geometryTypes === undefined) {
			return "";
		}
		if (preprocessedLayerMetadata.geometryTypes[LAYER_NUM].length > 1) {
			return "Geometry Types";
		}
		return "Geometry Type";
	}, [preprocessedLayerMetadata]);

	// Generate formatted string listing geometry type(s)
	const geometryTypesFmt = useMemo(() => {
		if (preprocessedLayerMetadata.geometryTypes === undefined) {
			return "";
		}
		const geomTypes = preprocessedLayerMetadata.geometryTypes[LAYER_NUM];
		const fmt = geomTypes.length > 1 ? geomTypes.join(", ") : geomTypes[0];
		return fmt;
	}, [preprocessedLayerMetadata]);

	/*
	 * Callbacks
	 */

	// Handle header sort events in the preview attributes table
	const handlePreviewAttrTableSort = useCallback(
		(clickedColumn: string) => {
			if (previewAttrTableSortColumn !== clickedColumn) {
				setPreviewAttrTableSortColumn(clickedColumn);
				setPreviewAttrTableSortColumnDir(false);
			} else {
				setPreviewAttrTableSortColumnDir((prev) => !prev);
			}
		},
		[previewAttrTableSortColumn],
	);

	// Handle header sort events in the config attributes table
	const handleConfigAttrTableSort = useCallback(
		(clickedColumn: string) => {
			const newColumn = clickedColumn as ConfigAttrTableColumn;
			if (configAttrTableSortColumn !== newColumn) {
				setConfigAttrTableSortColumn(newColumn);
				setConfigAttrTableSortColumnDir(false);
			} else {
				setConfigAttrTableSortColumnDir((prev) => !prev);
			}
		},
		[configAttrTableSortColumn],
	);

	// Handle mapbox library load event
	const handleMapLoad = useCallback(() => {
		if (mapRef?.current && preProcessedGeoJson.features.length) {
			const [minLng, minLat, maxLng, maxLat] = bbox(preProcessedGeoJson);
			mapRef.current.fitBounds(
				[
					[minLng, minLat],
					[maxLng, maxLat],
				],
				{ padding: 4, duration: 0 },
			);
		}
	}, [preProcessedGeoJson]);

	// Handle change in multi-selection of "Select attribute columns to preview in table"
	const handleMapAttributesTargetChange = useCallback(
		(_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newTargetAttrs = data.value as AttrVis;
			setMapAttributesTarget(newTargetAttrs);
		},
		[],
	);

	// Handle change in multi-selection of "Select attribute columns to preview in table"
	const handleSourceAttrColsChange = useCallback((_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
		const newSelectedAttCols = data.value as string[];
		setSelectedSourceAttrCols(newSelectedAttCols);
	}, []);

	// Handle change in DDL "Select one attribute column to assign new values"
	const handleSelectedAttrColChange = useCallback(
		(_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newSelectedAttrCol = data.value as string;
			setSelectedAttrCol(newSelectedAttrCol);

			// fill in [0, unassigned] for each value in valuesMapping
			const sortedUniqueAttrVals = getSortedUniqueAttrVals(newSelectedAttrCol);
			const defaultMapping: VectorDataLayerValuesMapping[] = sortedUniqueAttrVals.map((attrVal, i) => {
				if (numZones && i < numZones) {
					const id = i + 1;
					return [attrVal, id, `(enter description for ID ${id})`] as VectorDataLayerValuesMapping;
				} else {
					return [attrVal, UNASSIGNED_ID, UNASSIGNED_DESCR] as VectorDataLayerValuesMapping;
				}
			});
			setValuesMapping(defaultMapping);

			// clamp numZones to # of unique values appearing in this attribute column
			const n = _.min([sortedUniqueAttrVals.length, MAX_ZONES]);
			setNumZones(n);
			setFormIsDirty(true);
		},
		[numZones, getSortedUniqueAttrVals],
	);

	// Handle change in DDL "Select number of categories (zones) for attribute assignment"
	const handleSelectedNumZonesChange = useCallback((_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
		const n = Number(data.value);
		setNumZones(n);
		// update valuesMapping with [0, unassigned] for any values which are higher than this # zones
		setValuesMapping((prev) => {
			const newValuesMapping = prev.map((mappingRec) => {
				const [srcVal, transId, transDescr] = mappingRec;
				if (transId > n) {
					return [srcVal, UNASSIGNED_ID, UNASSIGNED_DESCR] as VectorDataLayerValuesMapping;
				}
				return [srcVal, transId, transDescr] as VectorDataLayerValuesMapping;
			});
			return newValuesMapping;
		});
		setFormIsDirty(true);
	}, []);

	// Handle change in the ID column of Categorical Assignment of attributes
	const handleValuesMappingIDChange = useCallback(
		(srcVal: string | number | boolean, _evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newId = Number(data.value);
			if (Number.isNaN(newId)) {
				return;
			}
			setValuesMapping((prev) => {
				let nextMapping = prev.map(([srcValCmp, stdId, stdDescr]) => {
					if (srcVal === srcValCmp) {
						let newDescr = stdDescr;
						// 0 is a reserved value, so description must be "unassigned"
						if (newId === UNASSIGNED_ID) {
							newDescr = UNASSIGNED_DESCR;
						} else {
							// fill in Descr if we already have this ID in the valuesMapping
							const matchStdId = _.find(prev, ([srcValCmp, stdId]) => stdId === newId && srcValCmp !== srcVal);
							if (matchStdId) {
								newDescr = matchStdId[2];
							} else {
								newDescr = `(enter description for ID ${newId})`;
							}
						}
						return [srcVal, newId, newDescr] as VectorDataLayerValuesMapping;
					}
					return [srcValCmp, stdId, stdDescr] as VectorDataLayerValuesMapping;
				});

				// enforce reserved "unassigned" description for ID 0 across all rows
				if (newId === UNASSIGNED_ID) {
					nextMapping = nextMapping.map(([srcValCmp, stdId, stdDescr]) => {
						if (stdId === UNASSIGNED_ID) {
							return [srcValCmp, stdId, UNASSIGNED_DESCR] as VectorDataLayerValuesMapping;
						}
						return [srcValCmp, stdId, stdDescr] as VectorDataLayerValuesMapping;
					});
				}

				return nextMapping;
			});
			setFormIsDirty(true);
		},
		[],
	);

	// Handle change in the Description column of Categorical Assignment of attributes table
	const handleValuesMappingDescrChange = useCallback(
		(srcVal: string | number | boolean, _evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const rawDescr = typeof data.value === "string" ? data.value : String(data.value ?? "");
			const inputDescr = rawDescr.trim();
			if (!inputDescr) {
				return;
			}
			setValuesMapping((prev) => {
				const current = _.find(prev, ([srcValCmp]) => srcValCmp === srcVal);
				if (!current) {
					return prev;
				}

				const currentStdId = current[1];
				let nextStdId = currentStdId;
				let nextDescr = inputDescr;

				// "unassigned" is a reserved descr, so id must be 0.
				if (inputDescr === UNASSIGNED_DESCR) {
					nextStdId = UNASSIGNED_ID;
					nextDescr = UNASSIGNED_DESCR;
				} else {
					// fill in Id if we already have this Descr in the valuesMapping
					const matchStdDescr = _.find(
						prev,
						([srcValCmp, , stdDescr]) => stdDescr === inputDescr && srcValCmp !== srcVal,
					);
					if (matchStdDescr) {
						nextStdId = matchStdDescr[1];
					}
				}

				let nextMapping: VectorDataLayerValuesMapping[] = prev.map(([srcValCmp, stdId, stdDescr]) => {
					if (srcValCmp === srcVal) {
						return [srcValCmp, nextStdId, nextDescr] as VectorDataLayerValuesMapping;
					}
					return [srcValCmp, stdId, stdDescr] as VectorDataLayerValuesMapping;
				});

				// Keep descriptions consistent within an ID group when user edits the description for that group.
				if (nextStdId === currentStdId) {
					nextMapping = nextMapping.map(([srcValCmp, stdId, stdDescr]) => {
						if (stdId === nextStdId) {
							return [srcValCmp, stdId, nextDescr] as VectorDataLayerValuesMapping;
						}
						return [srcValCmp, stdId, stdDescr] as VectorDataLayerValuesMapping;
					});
				}

				// enforce reserved "unassigned" description for ID 0 across all rows
				if (nextStdId === UNASSIGNED_ID) {
					nextMapping = nextMapping.map(([srcValCmp, stdId, stdDescr]) => {
						if (stdId === UNASSIGNED_ID) {
							return [srcValCmp, stdId, UNASSIGNED_DESCR] as VectorDataLayerValuesMapping;
						}
						return [srcValCmp, stdId, stdDescr] as VectorDataLayerValuesMapping;
					});
				}

				return nextMapping;
			});
			setFormIsDirty(true);
		},
		[],
	);

	/**
	 * Send PUT request with new layer configuration info. Issue callback to parent component
	 * with response payload: Std GeoJson.
	 */
	const handleSaveRequest = useCallback(async () => {
		setIsSaving(true);

		const uuid = upload.id;
		const toastId = `vector-configurator-put-${uuid}`;
		toast.loading(`Processing vector data layer upload...`, { toastId, type: "info" });

		// let UI display toast before starting PUT request
		await new Promise<void>((resolve) => setTimeout(resolve, 1));

		const clientId = userAuth.currentClientId;
		const request: VectorDataLayerRequest = {
			uuid,
			layerNumber: 0,
			attributeFieldName: selectedAttrCol!,
			numZones: numZones!,
			valuesMapping,
		};
		try {
			const result = await updateVectorDataLayerUploadMutationAsync({
				getAccessToken: getTokenSilently,
				clientId,
				request,
			});
			toast.update(toastId, { render: "Done", type: "success", isLoading: false, autoClose: 1000 });
			onSaved(upload.id, result);
		} catch (error: unknown) {
			toast.dismiss(toastId);
			if (error instanceof ApiError && error.body?.messages) {
				const messages = error.body.messages as string[];
				toast.error(messages.join("; "));
			} else if (error instanceof Error) {
				toast.error(error.message);
			}
			console.error(error);
		}
		setIsSaving(false);
		setFormIsDirty(false);
	}, [
		upload.id,
		userAuth.currentClientId,
		selectedAttrCol,
		numZones,
		valuesMapping,
		updateVectorDataLayerUploadMutationAsync,
		getTokenSilently,
		onSaved,
	]);

	const handleValidationRequest = useCallback(() => {
		setIsValidationMsgShown(true);
	}, []);

	/*
	 * Rendering
	 */

	return (
		<>
			<Modal
				open={true}
				closeOnDimmerClick={false}
				closeOnEscape={false}
				size="fullscreen"
				className={styles.configuratorModal}
			>
				<Modal.Header className={styles.headerWithButtons}>
					<span>Vector Data Layer Configuration</span>
					<div>
						{validationMsg && (
							<Button
								content="Validate Configuration"
								icon="warning"
								compact
								onClick={handleValidationRequest}
								disabled={!!validationMsg && isValidationMsgShown}
							/>
						)}
						{!validationMsg && (
							<Button
								content="Save"
								icon={isSaving ? "spinner" : "checkmark"}
								compact
								positive
								onClick={() => withFieldAssociationConfirmation(() => void handleSaveRequest())}
								disabled={isSaving || !formIsDirty}
							/>
						)}
						<Button content="Cancel" icon="cancel" compact onClick={onCancel} negative />
					</div>
				</Modal.Header>
				<Modal.Content basic>
					<Form size="tiny" error={!!validationMsg && isValidationMsgShown}>
						<Segment vertical basic>
							<SegmentGroup horizontal basic className={styles.segmentGroupHorizontal}>
								<Segment basic className={styles.infoSegment}>
									<Grid columns="2" divided="vertically" relaxed={false} padded={"horizontally"}>
										<Grid.Row>
											<Grid.Column width={4}>Vector Data Type</Grid.Column>
											<Grid.Column width={12}>{vectorDataTypeLabel}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>File Type</Grid.Column>
											<Grid.Column width={12}>{preprocessedLayerMetadata.vectorDataFormat}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>{fileNamesFmt}</Grid.Column>
											<Grid.Column width={12}>{fileNames}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>Feature Count</Grid.Column>
											<Grid.Column width={12}>{featureCountFmt}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>{geometryTypesLabelFmt}</Grid.Column>
											<Grid.Column width={12}>{geometryTypesFmt}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>Select Category Number</Grid.Column>
											<Grid.Column width={12}>
												<Tooltip overlay="Select number of categories (zones) for attribute assignment" placement="top">
													<Form.Select
														compact
														placeholder="Select # zones..."
														options={categoryNumberSelectOptions}
														value={String(numZones)}
														onChange={handleSelectedNumZonesChange}
														error={
															isValidationMsgShown && validationMsg?.[1] === ValidationSection.SelectCategoryNumber
														}
													/>
												</Tooltip>
											</Grid.Column>
										</Grid.Row>
									</Grid>
								</Segment>
								<Segment basic className={styles.mapSegment}>
									<div className={styles.mapContainer}>
										<div className={`${styles.mapOverlay} ${styles.mapOverlayTopleft}`}>
											<Form.Select
												compact
												options={MAP_VIS_ATTR_TARGET_OPTIONS}
												value={mapAttributesTarget}
												onChange={handleMapAttributesTargetChange}
											/>
										</div>
										<Map
											ref={mapRef}
											onLoad={handleMapLoad}
											projection="mercator"
											mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
											mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
										>
											<Source type="geojson" data={previewGeoJson}>
												<Layer {...mapLayerStyles} />
											</Source>
										</Map>
									</div>
								</Segment>
							</SegmentGroup>
							{validationMsg && isValidationMsgShown && (
								<Message content={validationMsg[0]} error compact icon="info circle" className={styles.validationMsg} />
							)}
							<SegmentGroup horizontal basic className={styles.segmentGroupHorizontal}>
								<Segment basic className={styles.infoSegment}>
									<Header size="small">
										Preview Attribute Values From Data Source
										{topNGeoJsonFeaturesDescr}
										<Tooltip overlay="Select attribute columns to preview in table" placement="top">
											<Form.Dropdown
												fluid
												multiple
												selection
												search
												placeholder="Select attribute columns to preview in table"
												options={sourceAttrColsSelectOptions}
												value={selectedSourceAttrCols}
												onChange={handleSourceAttrColsChange}
											/>
										</Tooltip>
									</Header>
									<div className={styles.tableScrollContainer}>
										<Table celled striped sortable selectable compact textAlign="center">
											<Table.Header>
												<Table.HeaderCell></Table.HeaderCell>
												{selectedSourceAttrCols.map((attrCol) => (
													<Table.HeaderCell
														key={attrCol}
														collapsing
														sorted={
															previewAttrTableSortColumn === attrCol
																? previewAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
														onClick={() => handlePreviewAttrTableSort(attrCol)}
													>
														{attrCol}
													</Table.HeaderCell>
												))}
											</Table.Header>
											<Table.Body>
												{topNGeoJsonFeatures.map((feature) => (
													<Table.Row key={feature.properties.previewAttrsColorIdx}>
														<Table.Cell collapsing>
															<ColorChip bgColor={sourceFeatureColors[feature.properties.previewAttrsColorIdx]} />
														</Table.Cell>
														{selectedSourceAttrCols.map((attrCol) => (
															<Table.Cell key={attrCol} collapsing>
																{feature.properties[attrCol] as React.ReactNode}
															</Table.Cell>
														))}
													</Table.Row>
												))}
											</Table.Body>
										</Table>
									</div>
								</Segment>
								<Segment basic className={styles.infoSegment}>
									<Header size="small">
										{numZones}-zone Categorical Assignment of Attributes ({selectedAttrCol}&nbsp;&rarr;&nbsp;ID)
										<Tooltip overlay="Select one attribute column to assign new values" placement="top">
											<Dropdown
												fluid
												selection
												search
												placeholder="Select one attribute column to assign new values"
												options={sourceAttrColsSelectOptions}
												value={selectedAttrCol}
												onChange={handleSelectedAttrColChange}
											/>
										</Tooltip>
									</Header>
									<div className={styles.tableScrollContainer}>
										<Table celled striped sortable selectable compact textAlign="center">
											<Table.Header>
												<Table.HeaderCell> </Table.HeaderCell>
												<Tooltip overlay={"Source attribute values from selected column"} placement="top">
													<Table.HeaderCell
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.SRC_VALS)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.SRC_VALS
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														{selectedAttrCol}
													</Table.HeaderCell>
												</Tooltip>
												<Tooltip overlay={attributeAssignmentTooltip} placement="top">
													<Table.HeaderCell
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_IDS)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.STD_IDS
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														ID
													</Table.HeaderCell>
												</Tooltip>
												<Tooltip overlay="aerialFARMS description of zone/category" placement="top">
													<Table.HeaderCell
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_DESCR)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.STD_DESCR
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														Description
													</Table.HeaderCell>
												</Tooltip>
											</Table.Header>
											<Table.Body>
												{sortedValuesMapping.map(([srcVal, stdId, stdDescr]) => (
													<Table.Row key={String(srcVal)}>
														<Table.Cell collapsing>
															{targetAttrColors[stdId] === TRANSPARENT_COLOR ? (
																<TransparentColorChip />
															) : (
																<ColorChip bgColor={targetAttrColors[stdId] || "grey"} />
															)}
														</Table.Cell>
														<Table.Cell collapsing>{srcVal}</Table.Cell>
														<Table.Cell collapsing>
															<Form.Dropdown
																compact
																fluid
																selection
																options={targetAttrValuesSelectIDOptions}
																value={String(stdId)}
																onChange={(e, data) => handleValuesMappingIDChange(srcVal, e, data)}
																error={isValidationMsgShown && validationMsg?.[1] === ValidationSection.IdColumn}
															/>
														</Table.Cell>
														<Table.Cell collapsing>
															<Form.Dropdown
																compact
																fluid
																search
																selection
																allowAdditions
																options={targetAttrValuesSelectDescrOptions}
																value={stdDescr}
																onChange={(e, data) => handleValuesMappingDescrChange(srcVal, e, data)}
																error={
																	isValidationMsgShown && validationMsg?.[1] === ValidationSection.DescriptionColumn
																}
															/>
														</Table.Cell>
													</Table.Row>
												))}
											</Table.Body>
										</Table>
									</div>
								</Segment>
							</SegmentGroup>
						</Segment>
					</Form>
				</Modal.Content>
			</Modal>

			<SimpleOkCancelModal
				headerMsg={FIELD_ASSOC_RESET_HEADER}
				bodyMsg={FIELD_ASSOC_RESET_BODY}
				open={fieldAssocModalIsOpen}
				onOk={handleFieldAssocModalOk}
				onCancel={handleFieldAssocModalCancel}
			/>
		</>
	);
};
export default VectorConfiguratorModal;

export const COLOR_BREWER2 = {
	Set3: {
		"3": ["rgb(141,211,199)", "rgb(255,255,179)", "rgb(190,186,218)"],
		"4": ["rgb(141,211,199)", "rgb(255,255,179)", "rgb(190,186,218)", "rgb(251,128,114)"],
		"5": ["rgb(141,211,199)", "rgb(255,255,179)", "rgb(190,186,218)", "rgb(251,128,114)", "rgb(128,177,211)"],
		"6": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
		],
		"7": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
		],
		"8": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
			"rgb(252,205,229)",
		],
		"9": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
			"rgb(252,205,229)",
			"rgb(217,217,217)",
		],
		"10": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
			"rgb(252,205,229)",
			"rgb(217,217,217)",
			"rgb(188,128,189)",
		],
		"11": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
			"rgb(252,205,229)",
			"rgb(217,217,217)",
			"rgb(188,128,189)",
			"rgb(204,235,197)",
		],
		"12": [
			"rgb(141,211,199)",
			"rgb(255,255,179)",
			"rgb(190,186,218)",
			"rgb(251,128,114)",
			"rgb(128,177,211)",
			"rgb(253,180,98)",
			"rgb(179,222,105)",
			"rgb(252,205,229)",
			"rgb(217,217,217)",
			"rgb(188,128,189)",
			"rgb(204,235,197)",
			"rgb(255,237,111)",
		],
		type: "qual",
	},
	Greys: {
		"3": ["rgb(240,240,240)", "rgb(189,189,189)", "rgb(99,99,99)"],
		"4": ["rgb(247,247,247)", "rgb(204,204,204)", "rgb(150,150,150)", "rgb(82,82,82)"],
		"5": ["rgb(247,247,247)", "rgb(204,204,204)", "rgb(150,150,150)", "rgb(99,99,99)", "rgb(37,37,37)"],
		"6": [
			"rgb(247,247,247)",
			"rgb(217,217,217)",
			"rgb(189,189,189)",
			"rgb(150,150,150)",
			"rgb(99,99,99)",
			"rgb(37,37,37)",
		],
		"7": [
			"rgb(247,247,247)",
			"rgb(217,217,217)",
			"rgb(189,189,189)",
			"rgb(150,150,150)",
			"rgb(115,115,115)",
			"rgb(82,82,82)",
			"rgb(37,37,37)",
		],
		"8": [
			"rgb(255,255,255)",
			"rgb(240,240,240)",
			"rgb(217,217,217)",
			"rgb(189,189,189)",
			"rgb(150,150,150)",
			"rgb(115,115,115)",
			"rgb(82,82,82)",
			"rgb(37,37,37)",
		],
		"9": [
			"rgb(255,255,255)",
			"rgb(240,240,240)",
			"rgb(217,217,217)",
			"rgb(189,189,189)",
			"rgb(150,150,150)",
			"rgb(115,115,115)",
			"rgb(82,82,82)",
			"rgb(37,37,37)",
			"rgb(0,0,0)",
		],
		type: "seq",
	},
};
